type ColorScheme = 'light' | 'dark';

type CbarMenuItemElement = HTMLElement & {
  title: string;
  headline?: string;
};

type CbarHeaderElement = HTMLElement;

type ThemeAppliedEvent = CustomEvent<{
  resolvedScheme?: ColorScheme;
}>;

const TOGGLE_SELECTOR = '[data-cbar-theme-mode-toggle]';

export function getResolvedColorScheme(
  root: HTMLElement = document.documentElement
): ColorScheme {
  const value = root.getAttribute('data-color-scheme');
  return value === 'dark' ? 'dark' : 'light';
}

/** Label for the scheme you switch to (Dark when currently light, Light when currently dark). */
export function getThemeToggleLabel(resolved: ColorScheme): string {
  return resolved === 'dark' ? 'Light' : 'Dark';
}

/** Material icon for the scheme you switch to (light_mode / dark_mode). */
export function getThemeToggleIcon(resolved: ColorScheme): string {
  return resolved === 'dark' ? 'light_mode' : 'dark_mode';
}

export function getNextColorScheme(resolved: ColorScheme): ColorScheme {
  return resolved === 'dark' ? 'light' : 'dark';
}

export function findShellHeader(): CbarHeaderElement | null {
  const shell = document.querySelector('#app-shell, #admin-shell');
  const header =
    shell?.shadowRoot?.querySelector('cbar-header') ??
    document.querySelector('cbar-header');

  return header as CbarHeaderElement | null;
}

function findThemeElement(): HTMLElement | null {
  return document.querySelector('cbar-theme');
}

function updateThemeTogglePresentation(resolved: ColorScheme): void {
  const label = getThemeToggleLabel(resolved);
  const icon = getThemeToggleIcon(resolved);

  document.querySelectorAll(TOGGLE_SELECTOR).forEach((node) => {
    const item = node as CbarMenuItemElement;
    item.title = label;
    item.headline = label;
  });

  document.querySelectorAll('[data-cbar-theme-mode-icon]').forEach((node) => {
    node.textContent = icon;
  });
}

function bindThemeToggle(themeElement: HTMLElement): void {
  document.querySelectorAll(TOGGLE_SELECTOR).forEach((node) => {
    if (node.getAttribute('data-theme-toggle-bound') === 'true') {
      return;
    }

    node.setAttribute('data-theme-toggle-bound', 'true');
    const toggle = node as CbarMenuItemElement;

    const applyLabel = () => {
      updateThemeTogglePresentation(getResolvedColorScheme());
    };

    const activate = () => {
      const resolved = getResolvedColorScheme();
      themeElement.setAttribute('color-scheme', getNextColorScheme(resolved));
      applyLabel();
    };

    toggle.addEventListener('click', activate);
    applyLabel();
  });
}

function bindHeaderAuthMenuItems(): void {
  const header = findShellHeader();

  document.querySelectorAll('[data-header-signup]').forEach((node) => {
    if (node.getAttribute('data-auth-menu-bound') === 'true') {
      return;
    }
    node.setAttribute('data-auth-menu-bound', 'true');
    node.addEventListener('click', () => {
      header?.dispatchEvent(
        new CustomEvent('signup-clicked', { bubbles: true, composed: true })
      );
    });
  });

  document.querySelectorAll('[data-header-signin]').forEach((node) => {
    if (node.getAttribute('data-auth-menu-bound') === 'true') {
      return;
    }
    node.setAttribute('data-auth-menu-bound', 'true');
    node.addEventListener('click', () => {
      header?.dispatchEvent(
        new CustomEvent('signin-clicked', { bubbles: true, composed: true })
      );
    });
  });
}

export async function setupThemeModeSwitcher(): Promise<void> {
  await Promise.all([
    customElements.whenDefined('cbar-theme'),
    customElements.whenDefined('cbar-menu-item'),
  ]);

  const themeElement = findThemeElement();
  if (!themeElement) {
    return;
  }

  window.addEventListener('cbar-theme-applied', (event: Event) => {
    const resolved =
      (event as ThemeAppliedEvent).detail?.resolvedScheme ?? getResolvedColorScheme();
    updateThemeTogglePresentation(resolved);
  });

  bindThemeToggle(themeElement);
  bindHeaderAuthMenuItems();
  updateThemeTogglePresentation(getResolvedColorScheme());
}
